import { ECardinality, IWFParameter } from "@kuika/kuika-cl-model";
import { KuikaSelectboxOption, KuikaSelectboxOptionGroup } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";

/**
 * Scalar backend types offered for workflow parameters. Shared by the create and edit dialogs so the
 * single unified type selector (scalars + Kuika Data Types in one dropdown) behaves identically in both.
 *
 * Every name must be a member of the backend's `EDataType`, which the workflow JSON deserialises into.
 * One that is not throws while the project is read, before generation starts, and that takes the whole
 * workspace's generation down — not just the workflow that used it. `Single` was offered here and did
 * exactly that; it is a column type, not a workflow parameter type.
 */
export const WORKFLOW_SCALAR_TYPES: string[] = [
  "Boolean",
  "DateTimeOffset",
  "Decimal",
  "Double",
  "Guid",
  "Int32",
  "Int64",
  "String"
];

const SCALAR_SET: Set<string> = new Set<string>(WORKFLOW_SCALAR_TYPES);

/** True when the selected type value is a scalar (vs a Kuika Data Type catalog reference). */
export function isScalarWorkflowType(value: string | undefined): boolean {
  return !!value && SCALAR_SET.has(value);
}

/**
 * Human-readable type label for a workflow parameter: the referenced Kuika Data Type for objects
 * (else the scalar), wrapped in `List<…>` for a list parameter. Shared by the parameters panel and the
 * step output-mapping dropdown so both reflect object/list types consistently.
 */
export function getWorkflowParamTypeLabel(p: IWFParameter): string {
  const base: string = p.isObject && p.dataTypeName ? p.dataTypeName : String(p.dataType);
  return p.cardinality === ECardinality.Many ? `List<${base}>` : base;
}

/**
 * Builds the grouped option list for the unified type selector: a "Scalar" group plus, when the
 * project has any, a "Data Types" group of catalog object types.
 */
export function buildWorkflowTypeGroups(catalogTypeNames: string[]): KuikaSelectboxOptionGroup[] {
  const groups: KuikaSelectboxOptionGroup[] = [
    {
      label: "Primitive",
      options: WORKFLOW_SCALAR_TYPES.map((t: string): KuikaSelectboxOption => ({ value: t, label: t }))
    }
  ];
  if (catalogTypeNames.length > 0) {
    groups.push({
      label: "Data Types",
      options: catalogTypeNames.map((n: string): KuikaSelectboxOption => ({ value: n, label: n }))
    });
  }
  return groups;
}
